/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect } from 'react';
import { ProfileAvatar } from 'ui/components';
import { DomainUtils, Ducks, Role, User } from '@wit/vtv-x-foundation-lib';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { PrivateRoutePath } from 'routes/paths';
import RouterUtils from 'services/utils/RouterUtils';
import * as ParentalControl from '../../../core/store/ducks/parentalControl';
import translate from '../../../services/i18n/translate';

const SettingsLandingPage: React.FunctionComponent = () => {
  // global store state
  const isConverged = useSelector(Ducks.Household.Selectors.data.getIsConverged);
  // Select Current USER
  const users: User[] = useSelector(Ducks.Household.Selectors.data.getUsers);
  // Current active profile
  const selectedUserId = useSelector(Ducks.Credentials.Selectors.getUserId);
  // User With Role:Default
  const defaultUser = useSelector(Ducks.Household.Selectors.data.getDefaultUser);
  const maxUsers = useSelector(Ducks.Household.Selectors.data.usersLimit);

  // Page State
  const [profiles, setProfiles] = React.useState<User[]>([]);
  const currentUsers = profiles.length;
  const history = useHistory();
  const [parentalPin, setParentalPin] = React.useState<boolean>(false);
  const [ageRatingOn, setAgeRatingOn] = React.useState<boolean>(false);

  // Parental Control - Age ratings Enabled/Disabled
  const activeAgeRating = useSelector(ParentalControl.Selectors.getAgeRating);
  useEffect(() => {
    if (defaultUser?.parentalValidation && activeAgeRating && activeAgeRating?.ratingValue > 0) {
      setAgeRatingOn(true);
    } else {
      setAgeRatingOn(false);
    }
  }, [activeAgeRating, defaultUser?.parentalValidation]);

  // Parental Control - Parental PIN Enable/Disable
  useEffect(() => {
    if (defaultUser?.parentalValidation) {
      setParentalPin(true);
    } else {
      setParentalPin(false);
    }
  }, [defaultUser?.parentalValidation]);

  // set profiles
  useEffect(() => {
    const selectableUsers = DomainUtils.getSelectableUsers(isConverged, users);
    // Sort Users By Firstname and only if they have a role "normal"
    const sortedUsers = selectableUsers.sort((a: User, b: User) => {
      if (a.firstName && a.role === Role.NORMAL && b.firstName && b.role === Role.NORMAL) {
        return a.firstName.localeCompare(b.firstName);
      }
      return 0;
    });
    setProfiles(sortedUsers);
  }, [isConverged, users]);

  const setProfileAndEdit = (profile: User) => {
    history.push(RouterUtils.getRouteWithDynamicValues(PrivateRoutePath.SETTINGS_PROFILES_EDIT, [profile.id]));
  };

  const profilesLine = (profile: User) => {
    const { id } = profile;
    const name = profile.role === Role.DEFAULT ? translate['profiles.household.name'] : profile.firstName || '';
    const isDefaultProfile = profile.role === Role.DEFAULT;
    const isSelectedProfile = selectedUserId === id;
    return (
      <li className="has-avatar" aria-hidden="false" key={`key-${id}`} id={`Settings_profile___${id}`}>
        <span className="setting-name value-white">
          <ProfileAvatar profileId={id} name={name} />
          <span
            className={`profile-label ${isSelectedProfile ? 'value-white' : 'value-grey'}`}
            id={`Settings_profileName___${id}`}
          >
            {name}
          </span>
        </span>
        {isSelectedProfile && !isDefaultProfile && (
          <a
            onClick={() => {
              setProfileAndEdit(profile);
            }}
            className="settings-edit"
            aria-hidden="true"
            id={`Settings_editProfile___${id}`}
          >
            <span className="hide-mobile">{translate.settingsEditProfile}</span>
          </a>
        )}
      </li>
    );
  };

  const ProfilesSection = () => {
    return (
      <ul className="settings-row" id="Settings_profileSection">
        {profiles.map((profile) => {
          return profilesLine(profile);
        })}

        <li className="profileShade has-avatar">
          <span className="setting-name">
            <span className="avatar avatarType1">
              <span className="newProfile" />
            </span>
            <span className="profile-label setting-value-highlight">
              <span className="setting-name-attr ">{translate.settingsProfilesInUse}</span>
              <span id="Settings_currentNumberOfUsers">&nbsp;{currentUsers}</span>
              <span className="profile-count-splitter">&nbsp;/&nbsp;</span>
              <span id="Settings_maxNumberOfUsers">{maxUsers}</span>
            </span>
          </span>
          {currentUsers < maxUsers && (
            <a
              onClick={() => {
                history.push(PrivateRoutePath.SETTINGS_PROFILES_ADD);
              }}
              className="settings-edit-plus"
              aria-hidden="false"
              id="Settings_button___addProfile"
            >
              <span className="profile-label hide-mobile">{translate.settingsAddnewprofile}</span>
            </a>
          )}
        </li>
      </ul>
    );
  };

  const ParentControlSection = () => {
    return (
      <li id="Settings_parentControl">
        <div className="settings-group-item">{translate.settingsParentalControls}</div>
        <ul className="settings-row">
          <li className="mobile">
            <span className="setting-name">
              <span className="setting-name-attr value-grey">{translate.settingsParentalControlsStatus}</span>
              <span className="setting-value-highlight" id="Settings_status___parentControl">
                &nbsp;{ageRatingOn ? translate.buttonOn : translate.buttonOff}
              </span>
            </span>
            <a
              className="settings-edit"
              onClick={() => {
                history.push(PrivateRoutePath.SETTINGS_PARENTAL);
              }}
              id="Settings_button___parentControl"
            >
              <span className="hide-mobile">{translate.settingsManageParentalControls}</span>
            </a>
          </li>
        </ul>
      </li>
    );
  };

  const PinSection = () => {
    return (
      <li id="Settings_pinSection">
        <div className="settings-group-item">{translate.settingPin}</div>
        <ul className="settings-row">
          <li className="mobile">
            <span className="setting-name">
              <span className="setting-name-attr value-grey">{translate['setting.parental.pin.status.label']}</span>
              <span className="setting-value-highlight" id="Settings_status___pinSection">
                &nbsp;{parentalPin ? translate.buttonOn : translate.buttonOff}
              </span>
            </span>

            <a
              className="settings-edit"
              onClick={() => {
                history.push(PrivateRoutePath.SETTINGS_PIN);
              }}
              id="Settings_button___pinSection"
            >
              <span className="hide-mobile">{translate.settingManagePin}</span>
            </a>
          </li>
        </ul>
      </li>
    );
  };

  const PlayBackSection = () => {
    return (
      <li id="Settings_playbackSection">
        <div className="settings-group-item">{translate.settingsPlayback}</div>
        <ul className="settings-row row-accessibility">
          <li className="mobile">
            <a
              id="Settings_button___playbackSection"
              className="settings-edit"
              onClick={() => {
                history.push(PrivateRoutePath.SETTINGS_ACCESSIBILITY);
              }}
            >
              <span className="hide-mobile">{translate.settingsManageAccessibility}</span>
            </a>
          </li>
        </ul>
      </li>
    );
  };

  return (
    <div className="component-inner advanced-settings-template" id="Settings">
      <h2>{translate.settingsTitle}</h2>
      <p>{translate.settingsSectionsInfo}</p>
      <span className="spinner" aria-hidden="true" />
      <ul className="settings-list" aria-hidden="false">
        <li>
          <div className="settings-group-item">{translate.settingsManageProfiles}</div>
          <ProfilesSection />
        </li>
        <ParentControlSection />
        <PinSection />
        <PlayBackSection />
      </ul>
    </div>
  );
};

export default SettingsLandingPage;
